import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from '@mui/material';
import {
  createConciliationBatch,
  deleteCompanyPayIn,
} from 'lib/api/conciliations';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import {
  hasAnyPermissions,
  showAlert,
  validateCompanyTypes,
} from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import Payment from '../Payment';
import PaymentConciliationAssgination from './Conciliate';
import FNCDialog from './FNC';
import OtherIncomesDialog from './OtherIncomes';
import SendToCxPDialog from './SendToCxP';
import PaymentConciliationSummary from './SummaryContainer';

const PaymentConciliationDialog = (props: any) => {
  const {
    open,
    setOpen,
    payIn,
    setPayIn,
    setOpenBankAccountModal,
    setUp,
    pagination,
  } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [openEdit, setOpenEdit] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openFNC, setOpenFNC] = useState(false);
  const [openResellConfirm, setOpenResellConfirm] = useState(false);
  const [factoringDocumentsSuggested, setFactoringDocumentsSuggested] =
    useState([]);
  const [openOtherIncomes, setOpenOtherIncomes] = useState(false);
  const [openSendToCxP, setOpenSendToCxP] = useState(false);

  const generateConciliationSavePayload = () => {
    const documentsToConciliate = factoringDocumentsSuggested.filter(
      (doc: any) => doc.amountToConciliate > 0,
    );
    const hasFund = documentsToConciliate.some(
      (doc: any) =>
        doc.fundName !== '' &&
        doc.fundName !== null &&
        doc.fundName !== undefined,
    );
    const payload = documentsToConciliate.map((doc: any) => ({
      documentId: doc.id,
      amount: doc.amountToConciliate,
      payInId: payIn.id,
      observation: doc.observation,
    }));
    return {
      payload,
      resellInDocuments: hasFund,
    };
  };

  const canDelete = () => {
    return (
      payIn.amountConciliated === 0 &&
      hasAnyPermissions(user, ['financing_payIn_delete'])
    );
  };

  const submitPayIn = async () => {
    try {
      setLoading(true);
      const { payload } = generateConciliationSavePayload();
      await createConciliationBatch(dispatch, payload);
      setOpen(false);
      showAlert(dispatch, 'success', 'Pago conciliado correctamente');
      await setUp({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    } catch (error: any) {
      if (error.response.data) {
        showAlert(dispatch, 'error', error.response.data.message);
      } else {
        showAlert(
          dispatch,
          'error',
          'Ha ocurrido un error al guardar la conciliación',
        );
      }
    }
    setLoading(false);
  };

  const handleSubmitClick = () => {
    const { resellInDocuments } = generateConciliationSavePayload();
    if (resellInDocuments) {
      setOpenResellConfirm(true);
    } else {
      void submitPayIn();
    }
  };

  const deletePayIn = async () => {
    try {
      setLoading(true);
      await deleteCompanyPayIn(dispatch, payIn.id);
      setOpen(false);
      await setUp({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al borrar el pago');
    }
    setLoading(false);
  };

  const amountToConciliate = factoringDocumentsSuggested.reduce(
    (acc: number, doc: any) => acc + (Number(doc.amountToConciliate) || 0),
    0,
  );

  const disabled =
    loading || payIn.amount - payIn.amountConciliated - amountToConciliate < 0;

  const canConciliate = () => {
    if (payIn.amount - payIn.amountConciliated - amountToConciliate < 0)
      return false;
    return hasAnyPermissions(user, ['financing_payIn_conciliate']);
  };

  const canConciliateOtherIncomes = () => {
    if (payIn.amount - payIn.amountConciliated <= 0) return false;
    return hasAnyPermissions(user, ['financing_payIn_conciliate']);
  };

  const canSendToCxP = () => {
    if (payIn.amount - payIn.amountConciliated <= 0) return false;
    return hasAnyPermissions(user, ['financing_payIn_send_to_cxp']);
  };

  const handleSendToCxP = () => {
    setOpenSendToCxP(true);
  };

  const handleOtherIncomes = () => {
    setOpenOtherIncomes(true);
  };

  return (
    <div>
      <Dialog
        maxWidth="xl"
        fullWidth
        open={open}
        onClose={() => setOpen(false)}
      >
        {loading ? (
          <div style={{ minHeight: 200 }}>
            <CenteredCircularProgress type="dialog" />
          </div>
        ) : (
          <DialogContent>
            <PaymentConciliationSummary
              payIn={payIn}
              bigSetup={setUp}
              setOpen={setOpen}
            />
            <Divider />

            <PaymentConciliationAssgination
              payIn={payIn}
              setPayIn={setPayIn}
              setOpen={setOpen}
              bigSetup={setUp}
              setDocumentsSuggested={setFactoringDocumentsSuggested}
              documentsSuggested={factoringDocumentsSuggested}
            />
          </DialogContent>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: 20,
          }}
        >
          <div style={{ display: 'flex', gap: 10 }}>
            <CustomButton
              onClick={() => setOpenDelete(true)}
              color="error"
              disabled={!canDelete()}
            >
              Borrar
            </CustomButton>
            <CustomButton
              onClick={() => {
                setOpenEdit(true);
              }}
              color="warning"
              disabled={payIn.amountConciliated !== 0}
            >
              Editar
            </CustomButton>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <CustomButton onClick={() => setOpen(false)} color="secondary">
              Salir
            </CustomButton>
            {payIn.status !== 'CONCILIATED' && (
              <CustomButton
                onClick={() => handleSendToCxP()}
                color="success"
                disabled={!canSendToCxP()}
              >
                Enviar a CxP
              </CustomButton>
            )}
            {validateCompanyTypes(user, 'FinancingHub') &&
              payIn.status !== 'CONCILIATED' && (
                <CustomButton
                  onClick={() => setOpenFNC(true)}
                  color="success"
                  disabled={
                    disabled || !hasAnyPermissions(user, ['financing_fnc_new'])
                  }
                >
                  FNC
                </CustomButton>
              )}
            {payIn.status !== 'CONCILIATED' && (
              <CustomButton
                onClick={() => handleOtherIncomes()}
                color="success"
                disabled={!canConciliateOtherIncomes()}
              >
                Otros ingresos
              </CustomButton>
            )}
            {payIn.status !== 'CONCILIATED' && (
              <CustomButton
                onClick={() => handleSubmitClick()}
                color="primary"
                disabled={!canConciliate()}
              >
                Guardar
              </CustomButton>
            )}
          </div>
        </div>
        <FNCDialog
          open={openFNC}
          setBigOpen={setOpen}
          setOpen={setOpenFNC}
          setUp={setUp}
          payIn={payIn}
          setPayIn={setPayIn}
        />
        <Payment
          open={openEdit}
          setOpen={setOpenEdit}
          setOpenConciliation={setOpen}
          payIn={payIn}
          setPayIn={setPayIn}
          setUp={setUp}
          setOpenBankAccountModal={setOpenBankAccountModal}
          pagination={pagination}
        />
        {openOtherIncomes && (
          <OtherIncomesDialog
            open={openOtherIncomes}
            setOpen={setOpenOtherIncomes}
            setBigOpen={setOpen}
            payIn={payIn}
            setUp={setUp}
          />
        )}
        {openSendToCxP && (
          <SendToCxPDialog
            open={openSendToCxP}
            setOpen={setOpenSendToCxP}
            setBigOpen={setOpen}
            payIn={payIn}
            setUp={setUp}
          />
        )}
        <Dialog
          open={openResellConfirm}
          onClose={() => setOpenResellConfirm(false)}
        >
          <DialogContent>
            <Typography variant="h4">Confirmar conciliación</Typography>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '20px 0px',
              }}
            >
              <Typography variant="body1">
                Se detectaron documentos con cesión a fondo. Al continuar, se
                generarán las CxP correspondientes y la recaudación propia de
                forma automática.
              </Typography>
            </div>
            <DialogActions>
              <CustomButton
                onClick={() => setOpenResellConfirm(false)}
                color="secondary"
              >
                Cancelar
              </CustomButton>
              <CustomButton
                onClick={() => {
                  setOpenResellConfirm(false);
                  void submitPayIn();
                }}
                color="primary"
              >
                Continuar
              </CustomButton>
            </DialogActions>
          </DialogContent>
        </Dialog>
        <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
          <DialogContent>
            <Typography variant="h4">Borrar pago #{payIn.id}</Typography>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '20px 0px',
              }}
            >
              <Typography variant="body1">
                ¿Estás seguro de querer borrar este pago?
              </Typography>
            </div>
            <DialogActions>
              <CustomButton
                onClick={() => setOpenDelete(false)}
                color="secondary"
              >
                Cancelar
              </CustomButton>
              <CustomButton onClick={() => deletePayIn()} color="error">
                Borrar
              </CustomButton>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </Dialog>
    </div>
  );
};

export default PaymentConciliationDialog;
